import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getDemoProfile } from '@/lib/demoData';
import type { AuthorityChain, BadgeReference } from '@/types/badges';

interface AuthorityChainViewProps {
  chain: AuthorityChain;
  onSelectGranter?: (pubkey: string) => void;
  className?: string;
}

export function AuthorityChainView({
  chain,
  onSelectGranter,
  className,
}: AuthorityChainViewProps) {
  // Build path from root to leaf
  const path: { pubkey: string; badge: BadgeReference; isGranter: boolean }[] = [];
  
  let current: AuthorityChain | undefined = chain;
  while (current) {
    path.unshift({
      pubkey: current.granter,
      badge: current.badge,
      isGranter: current === chain,
    });
    
    // Also add the recipient if we're at the end
    if (!current.parentChain && current !== chain) {
      path.unshift({
        pubkey: current.recipient,
        badge: current.badge,
        isGranter: false,
      });
    }
    
    current = current.parentChain;
  }
  
  // Add the final recipient
  path.push({
    pubkey: chain.recipient,
    badge: chain.badge,
    isGranter: false,
  });

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            Authority Chain
            {!chain.isValid && (
              <Badge variant="destructive" className="text-xs">Invalid</Badge>
            )}
          </CardTitle>
          {chain.invalidReason && (
            <Badge variant="outline" className="text-xs text-destructive">
              {chain.invalidReason}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col gap-1">
          {path.map((node, index) => {
            const profile = getDemoProfile(node.pubkey);
            const isLast = index === path.length - 1;
            
            return (
              <div key={node.pubkey + index} className="flex items-center gap-2">
                {/* Connector line */}
                {index > 0 && (
                  <div className="absolute left-5 w-0.5 h-5 bg-border -translate-x-1/2" 
                       style={{ marginTop: '-1.5rem' }} />
                )}
                
                {/* Avatar */}
                <div className="relative z-10">
                  <Avatar className={cn(
                    'h-8 w-8 border-2',
                    node.isGranter && 'border-amber-400',
                    !node.isGranter && !isLast && 'border-slate-300',
                    isLast && 'border-emerald-400'
                  )}>
                    <AvatarImage src={profile.picture} />
                    <AvatarFallback className="text-xs">
                      {node.pubkey.slice(0, 4)}
                    </AvatarFallback>
                  </Avatar>
                </div>
                
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={cn(
                      'text-sm font-medium truncate',
                      isLast && 'text-emerald-600 dark:text-emerald-400'
                    )}>
                      {profile.name}
                    </p>
                    {isLast && (
                      <Badge className="text-xs bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
                        Recipient
                      </Badge>
                    )}
                    {index === 0 && (
                      <Badge className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                        Origin
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-mono">{node.pubkey.slice(0, 8)}...</span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <BadgeIcon className="w-3 h-3" />
                      <span className="capitalize">{node.badge.identifier.replace(/-/g, ' ')}</span>
                      <span className="text-muted-foreground">(rank {node.badge.rank})</span>
                    </span>
                  </div>
                </div>
                
                {/* Action button for granters */}
                {index < path.length - 1 && onSelectGranter && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs"
                    onClick={() => onSelectGranter(node.pubkey)}
                  >
                    View
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

interface BadgeTierViewProps {
  rank: number;
  badgeName: string;
  badgeImage: string;
  holders: {
    pubkey: string;
    isValid: boolean;
  }[];
  className?: string;
}

export function BadgeTierView({
  rank,
  badgeName,
  badgeImage,
  holders,
  className,
}: BadgeTierViewProps) {
  const rankColors = {
    0: 'from-amber-500 to-orange-600',
    1: 'from-purple-500 to-violet-600',
    2: 'from-emerald-500 to-teal-600',
    3: 'from-blue-500 to-cyan-600',
    4: 'from-slate-500 to-gray-600',
  };

  const bgColor = rankColors[rank as keyof typeof rankColors] || rankColors[4];

  return (
    <div className={cn('rounded-xl border bg-card overflow-hidden', className)}>
      {/* Header */}
      <div className={cn(
        'bg-gradient-to-r p-4 text-white',
        bgColor
      )}>
        <div className="flex items-center gap-4">
          <img
            src={badgeImage}
            alt={badgeName}
            className="w-16 h-16 rounded-full border-2 border-white/30 shadow-lg object-cover"
          />
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-xl font-bold">{badgeName}</h3>
              <Badge className="bg-white/20 text-white border-0">
                Rank {rank}
              </Badge>
            </div>
            <p className="text-sm text-white/80">
              {holders.length} holder{holders.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Holders */}
      <div className="p-4">
        <div className="space-y-3">
          {holders.slice(0, 5).map((holder) => {
            const profile = getDemoProfile(holder.pubkey);
            return (
              <div
                key={holder.pubkey}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={profile.picture} />
                  <AvatarFallback className="text-xs">
                    {holder.pubkey.slice(0, 4)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{profile.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">
                    {holder.pubkey.slice(0, 12)}...
                  </p>
                </div>
                {!holder.isValid && (
                  <Badge variant="destructive" className="text-xs">
                    Invalid
                  </Badge>
                )}
              </div>
            );
          })}
          {holders.length > 5 && (
            <p className="text-sm text-muted-foreground text-center py-2">
              +{holders.length - 5} more
            </p>
          )}
          {holders.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No holders yet
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function BadgeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}
